import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Spin, Button, Input, Divider, notification, Modal, Select, Avatar, Tag, Tooltip, Radio, Typography, Upload, Progress, Tabs, DatePicker } from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import { FileExcelOutlined, EyeOutlined, UserOutlined, SaveOutlined, CloseOutlined, EditOutlined, PhoneOutlined, SoundOutlined, HistoryOutlined, UploadOutlined, ReloadOutlined, ClearOutlined } from "@ant-design/icons";
import userDummy from "../../assets/images/dummy-user.png";
import { hasPermission } from '../../components/permissions';
import '../../assets/styles/custom_style.css';
import moment from 'moment';
import * as XLSX from 'xlsx';
import 'antd/dist/antd.css';

const { Option } = Select;

const createStyledTitle = (text) => (
  <span style={{ fontSize: '13px', color: '#7a0000', fontWeight: 700 }}>{text}</span>
);

const statusColor = (status) => {
  if (status === 'verified') return { background: '#52c41a', color: '#fff' };
  if (status === 'failed')   return { background: '#ff4d4f', color: '#fff' };
  return { background: '#faad14', color: '#fff' };
};

function CallingReport() {
  const [userList, setUserList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);      // full-page (Excel export only)
  const [tableLoading, setTableLoading] = useState(false); // table-level loading
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phone: '',
    father_name: '',
    caste: '',
    gotra: '',
    village: '',
    district: '',
    tehsil: '',
    occ_district: '',
    announced_amount: '',
    max_announced_amount: '',
    start_joining_date: '',
    end_joining_date: '',
    last_payment_from: '',
    last_payment_to: '',
  });
  // ── Tab state ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('calling_list');

  // ── Call History Modal ───────────────────────────────────────────────────
  const [callHistoryVisible, setCallHistoryVisible] = useState(false);
  const [callHistoryData, setCallHistoryData] = useState([]);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);
  const [callHistoryUser, setCallHistoryUser] = useState(null);

  // ── Voice Call State ────────────────────────────────────────────────────
  const [voiceBroadcastVisible, setVoiceBroadcastVisible] = useState(false);
  const [vcAnnouncementId, setVcAnnouncementId] = useState('');
  const [vcCampaignName, setVcCampaignName] = useState('');
  const [vcFilterCount, setVcFilterCount] = useState(null);
  const [vcLoading, setVcLoading] = useState(false);
  const [vcScope, setVcScope] = useState('calling_list');
  const [vcCustomNumbers, setVcCustomNumbers] = useState('');

  const [individualCallVisible, setIndividualCallVisible] = useState(false);
  const [individualCallUser, setIndividualCallUser] = useState(null);
  const [icAnnouncementId, setIcAnnouncementId] = useState('');
  const [icLoading, setIcLoading] = useState(false);

  const [campaignsVisible, setCampaignsVisible] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsPagination, setCampaignsPagination] = useState({});
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const [reportVisible, setReportVisible] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // ── Announcement Management State ────────────────────────────────────────
  const [annUploadUrl, setAnnUploadUrl] = useState('');
  const [annUploading, setAnnUploading] = useState(false);
  const [annUploadedId, setAnnUploadedId] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [annLoading, setAnnLoading] = useState(false);
  // Manual add existing announcement
  const [annManualId, setAnnManualId] = useState('');
  const [annManualName, setAnnManualName] = useState('');
  const [annManualSaving, setAnnManualSaving] = useState(false);
  // Upload from computer
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  useEffect(() => {
    fetchAnnouncements();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      title: createStyledTitle("S.No"),
      dataIndex: "key",
      key: "key",
      fixed: 'left',
      width: 55,
      render: (text) => <span style={{ fontWeight: 600, fontSize: 12, color: '#7a0000' }}>{text}</span>,
    },
    {
      title: createStyledTitle("Name"),
      dataIndex: "name",
      key: "name",
      fixed: 'left',
      width: 220,
    },
    {
      title: createStyledTitle("Phone"),
      dataIndex: "phone_no",
      key: "phone_no",
      width: 130,
    },
    {
      title: createStyledTitle("Last Response"),
      key: "latest_payment_response",
      dataIndex: "latest_payment_response",
      width: 180,
      render: (text, record) => (
        isEditing(record.key) ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Input.TextArea
              value={editingResponse}
              onChange={(e) => setEditingResponse(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, text.user_id)}
              style={{ marginRight: 8, width: '200px', height: '60px' }}
            />
            <Button type="primary" onClick={() => saveResponse(text.user_id)} style={{ marginRight: 8 }}><SaveOutlined /></Button>
            <Button type="default" onClick={() => cancelEdit()}><CloseOutlined /></Button>
          </div>
        ) : (
          <div onDoubleClick={() => editResponse(record.key, text.latest_payment_response || '')} style={{ cursor: 'pointer', position: 'relative', paddingRight: 18 }}>
            <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{text.latest_payment_response || '—'}</span>
            <EditOutlined onClick={() => editResponse(record.key, text.latest_payment_response || '')} style={{ position: 'absolute', right: 0, top: 0, opacity: 0.5 }} />
          </div>
        )
      )
    },
    {
      title: createStyledTitle("Village"),
      dataIndex: "per_village",
      key: "per_village",
      width: 120,
    },
    {
      title: createStyledTitle("District"),
      dataIndex: "per_district",
      key: "per_district",
      width: 120,
    },
    {
      title: createStyledTitle("Occupation"),
      key: "occ_name",
      dataIndex: "occ_name",
      width: 130,
    },
    {
      title: createStyledTitle("Monthly Share"),
      key: "announced_amount",
      dataIndex: "announced_amount",
      width: 120,
    },
    {
      title: createStyledTitle("Joining Date"),
      key: "joining_date",
      dataIndex: "joining_date",
      width: 120,
    },
    {
      title: createStyledTitle("DOB"),
      key: "dob",
      dataIndex: "dob",
      width: 110,
    },
    {
      title: createStyledTitle("Gotra"),
      key: "gotra",
      dataIndex: "gotra",
      width: 110,
    },
    {
      title: createStyledTitle("Last Pay Date"),
      key: "last_payment_date",
      dataIndex: "last_payment_date",
      width: 120,
    },
    {
      title: createStyledTitle("Last Payment"),
      key: "last_payment_amount",
      dataIndex: "last_payment_amount",
      width: 120,
    },
    {
      title: createStyledTitle("Total Received"),
      key: "total_received_amount",
      dataIndex: "total_received_amount",
      width: 120,
    },
    {
      title: createStyledTitle("Cur. Year Duration"),
      key: "current_year_duration",
      dataIndex: "current_year_duration",
      width: 140,
    },
    {
      title: createStyledTitle("Total Duration"),
      key: "total_duration",
      dataIndex: "total_duration",
      width: 120,
    },
    {
      title: createStyledTitle("Up to Month"),
      key: "end_month",
      dataIndex: "end_month",
      width: 120,
    },
    {
      title: createStyledTitle("Status"),
      key: "member_status",
      dataIndex: "member_status",
      width: 100,
    },
    {
      title: createStyledTitle("Pay Status"),
      key: "last_payment_status",
      dataIndex: "last_payment_status",
      fixed: 'right',
      width: 110,
    },
    {
      title: createStyledTitle("Action"),
      key: "action",
      dataIndex: "action",
      fixed: 'right',
      width: 90,
    },
  ];

  const [editingKey, setEditingKey] = useState('');
  const [editingResponse, setEditingResponse] = useState('');
  const [editingResponseForBackup, setEditingResponseForBackup] = useState('');

  const isEditing = (key) => key === editingKey;

  const editResponse = (key, text) => {
    setEditingKey(key);
    setEditingResponse(text);
    setEditingResponseForBackup(text);
  };

  const cancelEdit = () => {
    setEditingKey('');
    setEditingResponse('');
    setEditingResponseForBackup('');
  };

  const saveResponse = async (key) => {
    try {
      if (editingResponse === editingResponseForBackup) {
        setEditingKey('');
        setEditingResponse('');
        setEditingResponseForBackup('');
      } else {
        const response = await axios.put(`${baseUrl}/users/updateResponse`, {
          user_id: key,
          latest_payment_response: editingResponse
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
        });
        if (response.data.status) {
          notification.success({ message: 'Response updated successfully', placement: 'topRight' });
          getUserList(currentPage, limit, sortField, sortOrder, search, filters, activeTab);
        } else {
          notification.error({ message: response.data.message, placement: 'topRight' });
        }
      }
    } catch (error) {
      notification.error({ message: error.response?.data?.message || error.message, placement: 'topRight' });
    }
    setEditingKey('');
    setEditingResponse('');
    setEditingResponseForBackup('');
  };

  const handleKeyDown = (e, userId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveResponse(userId);
    }
  };

  const debounce = (func, delay) => {
    let debounceTimer;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  };

  async function getUserList(page = 1, limit = 20, sortField = null, sortOrder = null, search = '', filters = {}, scope = null) {
    try {
      setCurrentPage(page);
      setLimit(limit);
      setTableLoading(true);
      const activeScope = scope || activeTab || 'calling_list';
      const filterParams = Object.keys(filters)
        .filter(key => filters[key])
        .map(key => `${key}=${encodeURIComponent(filters[key])}`)
        .join('&');
      const response = await axios.get(
        `${baseUrl}/voice-call/members?page=${page}&limit=${limit}&scope=${activeScope}&${filterParams}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (response.data.status) {
        setUserList(response.data.result);
        setPagination({
          current: page,
          pageSize: limit,
          total: response.data.totalCount
        });
      } else {
        notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.info({ message: 'Info', description: error.response?.data?.message, placement: 'topRight' });
    } finally {
      setTableLoading(false);
    }
  };

  const handleTableChange = (pagination, tableFilters, sorter) => {
    const { current, pageSize } = pagination;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(formattedOrder);
    getUserList(current, pageSize, field, formattedOrder, search, filters, activeTab);
  };

  const handleSearch = value => {
    setSearch(value);
    getUserList(currentPage, limit, sortField, sortOrder, value, filters, activeTab);
  };

  const handleLimitChange = value => {
    setLimit(value);
    getUserList(1, value, sortField, sortOrder, search, filters, activeTab);
  };

  const debouncedGetUserList = useCallback(debounce((page, limit, sortField, sortOrder, search, filters, scope) => {
    getUserList(page, limit, sortField, sortOrder, search, filters, scope);
  }, 600), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    debouncedGetUserList(currentPage, limit, sortField, sortOrder, search, filters, activeTab);
  }, [filters, currentPage, limit, sortField, sortOrder, search, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  const exportToExcel = async () => {
    setLoading(true);
    const filterParams = Object.keys(filters)
      .filter(key => filters[key])
      .map(key => `${key}=${encodeURIComponent(filters[key])}`)
      .join('&');
    const response = await axios.get(
      `${baseUrl}/voice-call/members?page=1&limit=5000&scope=${activeTab}&${filterParams}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    if (response.data.status) {
      setLoading(false);
      const formattedData = response.data.result.map((user, index) => ({
        "Sr.No": index + 1,
        "Phone no": user.phone_no,
        "Name": user.name_hn || '-',
        "Relation": user.relation_hn || '-',
        "Father / Husband Name": user.father_name_hn || '-',
        "Village": user.per_village_hn || '-',
        "Tehsil": user.per_tahsil_hn || '-',
        "District": user.per_district_hn || '-',
        "Occupation": user.occ_name || '-',
        "Occ District": user.occ_district || '-',
        "Joining Date": user.joining_date ? moment(user.joining_date).format('DD-MM-YYYY') : '-',
        "Monthly Share": user.announced_amount || '-',
        "DOB": user.dob ? moment(user.dob).format('DD-MM-YYYY') : '-',
        "Gotra": user.gotra || '-',
        "Total Duration": user.total_duration || '-',
        "Current Year Duration": user.current_year_duration || '-',
        "Last Payment Date": user.last_payment_date ? moment(user.last_payment_date).format('DD-MM-YYYY') : '-',
        "Last Payment Amount": user.last_payment_amount || '-',
        "Up to Month": user.end_month ? moment(user.end_month).format('MMMM YYYY') : '-',
        "Last Response": user.latest_payment_response || '-',
        "Total Received Amount": user.total_received_amount || '-',
        "Payment Status": user.last_payment_status || '-',

      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Calling Report");
      XLSX.writeFile(workbook, "Calling-Report.xlsx");
    } else {
      setLoading(false);
      notification.info({
        message: 'Info',
        description: response.data.message,
        placement: 'topRight'
      });
    }
  };

  // For show image on model large
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState("");

  const showModal = (image) => {
    setCurrentImage(image || userDummy);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const openCallHistory = async (user) => {
    setCallHistoryUser(user);
    setCallHistoryData([]);
    setCallHistoryVisible(true);
    setCallHistoryLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/member-history/${user.phone_no}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (resp.data.status) setCallHistoryData(resp.data.result || []);
    } catch (e) {
      notification.error({ message: e.message, placement: 'topRight' });
    } finally {
      setCallHistoryLoading(false);
    }
  };

  // ── Voice Call Handlers ──────────────────────────────────────────────────

  const fetchVcFilterCount = async (scope) => {
    try {
      if (scope === 'custom_numbers') { setVcFilterCount(null); return; }
      setVcFilterCount(null); // show counting...
      const params = Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
      const resp = await axios.get(`${baseUrl}/voice-call/filter-count?scope=${scope}&${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (resp.data.status) setVcFilterCount(resp.data.count);
    } catch (e) {
      console.error(e);
    }
  };

  const openVoiceBroadcastModal = (scope) => {
    const s = scope || activeTab || 'calling_list';
    setVcAnnouncementId('');
    setVcCampaignName('');
    setVcFilterCount(null);
    setVcScope(s);
    setVcCustomNumbers('');
    setVoiceBroadcastVisible(true);
    fetchVcFilterCount(s);
  };

  const handleVcScopeChange = (newScope) => {
    setVcScope(newScope);
    setVcFilterCount(null);
    fetchVcFilterCount(newScope);
  };

  const handleVoiceBroadcast = async () => {
    if (!vcAnnouncementId.trim()) {
      notification.warning({ message: 'Please enter the Announcement ID', placement: 'topRight' });
      return;
    }
    if (vcScope === 'custom_numbers' && !vcCustomNumbers.trim()) {
      notification.warning({ message: 'Please enter phone numbers to call', placement: 'topRight' });
      return;
    }
    setVcLoading(true);
    try {
      const resp = await axios.post(`${baseUrl}/voice-call/broadcast-filter`, {
        announcement_id: vcAnnouncementId.trim(),
        campaign_name: vcCampaignName || 'Bulk Voice Campaign',
        scope: vcScope,
        filters,
        custom_numbers: vcCustomNumbers
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });

      if (resp.data.status) {
        notification.success({
          message: 'Broadcast Initiated',
          description: resp.data.message,
          placement: 'topRight'
        });
        setVoiceBroadcastVisible(false);
      } else {
        notification.error({ message: 'Broadcast Failed', description: resp.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: 'Error', description: err.response?.data?.message || err.message, placement: 'topRight' });
    } finally {
      setVcLoading(false);
    }
  };

  const openIndividualCallModal = (user) => {
    setIndividualCallUser(user);
    setIcAnnouncementId('');
    setIndividualCallVisible(true);
  };

  const handleIndividualCall = async () => {
    if (!icAnnouncementId.trim()) {
      notification.warning({ message: 'Please enter the Announcement ID', placement: 'topRight' });
      return;
    }
    setIcLoading(true);
    try {
      const resp = await axios.post(`${baseUrl}/voice-call/broadcast-individual`, {
        announcement_id: icAnnouncementId.trim(),
        phone_no: individualCallUser.phone_no,
        user_id: individualCallUser.user_id,
        campaign_name: `Call: ${individualCallUser.name_display || individualCallUser.phone_no}`
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });

      if (resp.data.status) {
        notification.success({ message: 'Call Initiated', description: resp.data.message, placement: 'topRight' });
        setIndividualCallVisible(false);
      } else {
        notification.error({ message: 'Call Failed', description: resp.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: 'Error', description: err.response?.data?.message || err.message, placement: 'topRight' });
    } finally {
      setIcLoading(false);
    }
  };

  const loadCampaigns = async (page = 1, limit = 10) => {
    setCampaignsLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/campaigns?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (resp.data.status) {
        setCampaigns(resp.data.result);
        setCampaignsPagination({ current: page, pageSize: limit, total: resp.data.totalCount });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const openCampaignsModal = () => {
    setCampaignsVisible(true);
    loadCampaigns();
  };

  const fetchCampaignReport = async (campaignId) => {
    setReportLoading(true);
    setReportData(null);
    setReportVisible(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/report/${campaignId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (resp.data.status) {
        setReportData(resp.data);
      } else {
        notification.error({ message: 'Error', description: resp.data.message, placement: 'topRight' });
        setReportVisible(false);
      }
    } catch (e) {
      notification.error({ message: 'Error', description: e.message, placement: 'topRight' });
      setReportVisible(false);
    } finally {
      setReportLoading(false);
    }
  };

  // ── Announcement Management Handlers ──────────────────────────────────────
  const fetchAnnouncements = async () => {
    setAnnLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/announcement-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (resp.data.status) setAnnouncements(resp.data.result || []);
    } catch (_) {}
    finally { setAnnLoading(false); }
  };

  const handleAnnManualSave = async () => {
    if (!annManualId.trim()) {
      notification.warning({ message: 'Enter the Announcement ID', placement: 'topRight' }); return;
    }
    setAnnManualSaving(true);
    try {
      const resp = await axios.post(`${baseUrl}/voice-call/announcement-save`,
        { announcement_id: annManualId.trim(), announcement_name: annManualName.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (resp.data.status) {
        notification.success({ message: 'Announcement saved', placement: 'topRight' });
        setAnnManualId(''); setAnnManualName('');
        fetchAnnouncements();
      } else {
        notification.error({ message: resp.data.message, placement: 'topRight' });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: 'topRight' });
    } finally { setAnnManualSaving(false); }
  };

  const handleFileUpload = async (file) => {
    const allowed = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/x-wav'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['mp3', 'wav', 'ogg'].includes(ext)) {
      notification.error({ message: 'Only MP3, WAV or OGG files are allowed', placement: 'topRight' });
      return false;
    }
    setFileUploading(true);
    setFileUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('audio_file', file);
      const resp = await axios.post(`${baseUrl}/voice-call/upload-audio-file`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (e) => {
          setFileUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      if (resp.data.status) {
        notification.success({
          message: `Uploaded! Announcement ID: ${resp.data.announcement_id}`,
          description: resp.data.announcement_name,
          duration: 0,
          placement: 'topRight',
        });
        fetchAnnouncements();
      } else {
        notification.error({ message: resp.data.message, placement: 'topRight' });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: 'topRight' });
    } finally {
      setFileUploading(false);
      setFileUploadProgress(0);
    }
    return false; // prevent antd default upload
  };

  const handleAnnUpload = async () => {
    if (!annUploadUrl.trim()) {
      notification.warning({ message: 'Enter the announcement audio URL', placement: 'topRight' });
      return;
    }
    setAnnUploading(true);
    try {
      const resp = await axios.post(
        `${baseUrl}/voice-call/upload-announcement`,
        { announcement_url: annUploadUrl.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (resp.data.status) {
        notification.success({
          message: `Announcement ID: ${resp.data.announcement_id} — copy this ID to use in calls`,
          duration: 0,
          placement: 'topRight',
        });
        setAnnUploadedId(resp.data.announcement_id);
        setAnnUploadUrl('');
        fetchAnnouncements();
      } else {
        notification.error({ message: resp.data.message || 'Upload failed', placement: 'topRight' });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: 'topRight' });
    } finally {
      setAnnUploading(false);
    }
  };

  return (
<>
    {
      loading && (
        <div className="full-page-loader">
          <Spin size="large" />
        </div>
      )
    }
    <div className="tabled">

      {/* ── Announcement Management ────────────────────────────────────────── */}
      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card
            bordered={false}
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Announcement Management</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            bodyStyle={{ padding: '16px' }}
          >
            {/* ── Upload from Computer ── */}
            <Typography.Text strong style={{ fontSize: 13 }}>Upload from Computer</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Select an MP3/WAV file from your device — it will be uploaded to the server and registered with Aarvi automatically.
            </Typography.Text>
            <div style={{ marginBottom: 8 }}>
              <Upload
                accept=".mp3,.wav,.ogg"
                showUploadList={false}
                beforeUpload={handleFileUpload}
                disabled={fileUploading}
              >
                <Button icon={<UploadOutlined />} loading={fileUploading} type="primary">
                  {fileUploading ? 'Uploading...' : 'Choose MP3 / WAV File'}
                </Button>
              </Upload>
              {fileUploading && fileUploadProgress > 0 && (
                <Progress percent={fileUploadProgress} size="small" style={{ maxWidth: 300, marginTop: 8 }} />
              )}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* ── Upload via URL ── */}
            <Typography.Text strong style={{ fontSize: 13 }}>Upload via URL</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Already hosted online? Paste the public MP3/WAV URL to register it with Aarvi.
            </Typography.Text>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              <Input
                placeholder="https://cdn.example.com/payment-reminder.mp3"
                value={annUploadUrl}
                onChange={e => setAnnUploadUrl(e.target.value)}
                onPressEnter={handleAnnUpload}
                style={{ maxWidth: 460 }}
              />
              <Button icon={<UploadOutlined />} loading={annUploading} onClick={handleAnnUpload}>
                Upload &amp; Get ID
              </Button>
              {annUploadedId && (
                <Tag color="green" style={{ fontSize: 14, padding: '4px 12px' }}>
                  New ID: <strong>{annUploadedId}</strong>
                </Tag>
              )}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* ── Add existing Aarvi announcement manually ── */}
            <Typography.Text strong style={{ fontSize: 13 }}>Add Existing Aarvi Announcement</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Already uploaded to Aarvi panel? Save the ID here so it appears in the dropdown below.
            </Typography.Text>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              <Input
                placeholder="Announcement ID (e.g. 701519)"
                value={annManualId}
                onChange={e => setAnnManualId(e.target.value)}
                style={{ width: 200 }}
              />
              <Input
                placeholder="Name (e.g. Jhabra katha.mp3)"
                value={annManualName}
                onChange={e => setAnnManualName(e.target.value)}
                style={{ width: 260 }}
              />
              <Button type="default" loading={annManualSaving} onClick={handleAnnManualSave}>
                Save
              </Button>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* ── Saved announcements list ── */}
            <Typography.Text strong style={{ fontSize: 13 }}>Saved Announcements ({announcements.length})</Typography.Text>
            <Table
              size="small"
              rowKey="announcement_id"
              dataSource={announcements}
              loading={annLoading}
              pagination={{ pageSize: 5, size: 'small' }}
              style={{ marginTop: 8 }}
              columns={[
                { title: 'ID', dataIndex: 'announcement_id', key: 'announcement_id', width: 90,
                  render: t => <Tag color="blue" style={{ fontFamily: 'monospace' }}>{t}</Tag> },
                { title: 'Name', dataIndex: 'announcement_name', key: 'announcement_name',
                  render: t => <span style={{ fontSize: 13 }}>{t || '—'}</span> },
                { title: 'Use', key: 'use', width: 110,
                  render: (_, r) => (
                    <Button size="small" type="link" style={{ padding: 0 }}
                      onClick={() => {
                        setVcAnnouncementId(r.announcement_id);
                        setIcAnnouncementId(r.announcement_id);
                        notification.info({ message: `Announcement ID ${r.announcement_id} selected for next call`, placement: 'topRight', duration: 2 });
                      }}
                    >
                      Select
                    </Button>
                  )
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <Tabs
                activeKey={activeTab}
                onChange={(tab) => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
                style={{ color: '#fff' }}
                tabBarStyle={{ color: '#fff', borderBottom: 'none', marginBottom: 0 }}
                tabBarExtraContent={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 4 }}>
                    {hasPermission('/reports/calling-report', 'downloadPermission') && (
                      <Button size="small" icon={<FileExcelOutlined />} onClick={exportToExcel}>Excel</Button>
                    )}
                    {hasPermission('/reports/calling-report', 'editPermission') && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<SoundOutlined />}
                        onClick={() => openVoiceBroadcastModal(activeTab)}
                        style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      >
                        Bulk Call
                      </Button>
                    )}
                    <Button size="small" icon={<HistoryOutlined />} onClick={openCampaignsModal}>Campaigns</Button>
                  </div>
                }
              >
                <Tabs.TabPane tab={<span style={{ color: activeTab === 'calling_list' ? '#faad14' : '#bbb', fontWeight: 600, fontSize: 13, letterSpacing: 0.3 }}>Calling List</span>} key="calling_list" />
                <Tabs.TabPane tab={<span style={{ color: activeTab === 'paid_members' ? '#52c41a' : '#bbb', fontWeight: 600, fontSize: 13, letterSpacing: 0.3 }}>Paid Members</span>} key="paid_members" />
                <Tabs.TabPane tab={<span style={{ color: activeTab === 'inactive_members' ? '#ff7875' : '#bbb', fontWeight: 600, fontSize: 13, letterSpacing: 0.3 }}>Inactive</span>} key="inactive_members" />
                <Tabs.TabPane tab={<span style={{ color: activeTab === 'all_members' ? '#40a9ff' : '#bbb', fontWeight: 600, fontSize: 13, letterSpacing: 0.3 }}>All Members</span>} key="all_members" />
              </Tabs>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0'}}
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                loading={tableLoading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                bordered
                title={() => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Row gutter={[16, 16]}>
                      <Col span={4}>
                        <Input placeholder="Phone" value={filters.phone} onChange={e => handleFilterChange('phone', e.target.value)} />
                      </Col>
                      <Col span={4}>
                        <Input placeholder="Name" value={filters.name} onChange={e => handleFilterChange('name', e.target.value)} />
                      </Col>
                      <Col span={4}>
                        <Input placeholder="Father Name" value={filters.father_name} onChange={e => handleFilterChange('father_name', e.target.value)} />
                      </Col>
                      <Col span={3}>
                        <Input placeholder="Caste" value={filters.caste} onChange={e => handleFilterChange('caste', e.target.value)} />
                      </Col>
                      <Col span={3}>
                        <Input placeholder="Gotra" value={filters.gotra} onChange={e => handleFilterChange('gotra', e.target.value)} />
                      </Col>
                      <Col span={3}>
                        <Input placeholder="Village" value={filters.village} onChange={e => handleFilterChange('village', e.target.value)} />
                      </Col>
                      <Col span={3}>
                        <Input placeholder="District" value={filters.district} onChange={e => handleFilterChange('district', e.target.value)} />
                      </Col>
                      <Col span={3}>
                        <Input placeholder="Tehsil" value={filters.tehsil} onChange={e => handleFilterChange('tehsil', e.target.value)} />
                      </Col>
                      <Col span={3}>
                        <Input placeholder="Occ. District" value={filters.occ_district} onChange={e => handleFilterChange('occ_district', e.target.value)} />
                      </Col>
                      <Col span={8}>
                        <div className="amount-range-input">
                          <Input
                            placeholder="Min Monthly Share"
                            value={filters.announced_amount}
                            onChange={e => handleFilterChange('announced_amount', e.target.value)}
                            style={{ marginBottom: '8px' }}
                          />
                          <span className="range-separator">-</span>
                          <Input
                            placeholder="Max Monthly Share"
                            value={filters.max_announced_amount}
                            onChange={e => handleFilterChange('max_announced_amount', e.target.value)}
                          />
                        </div>
                      </Col>
                      {/* ── Joining Day Range ── */}
                      <Col span={8}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 600 }}>
                          Joining Day (1–31)
                          <Button
                            size="small"
                            type="link"
                            style={{ padding: '0 4px', fontSize: 11 }}
                            onClick={() => {
                              const today = new Date().getDate().toString();
                              setFilters(f => ({ ...f, start_joining_date: today, end_joining_date: today }));
                            }}
                          >
                            Today ({new Date().getDate()})
                          </Button>
                          {(filters.start_joining_date || filters.end_joining_date) && (
                            <Button
                              size="small"
                              type="link"
                              danger
                              style={{ padding: '0 4px', fontSize: 11 }}
                              onClick={() => setFilters(f => ({ ...f, start_joining_date: '', end_joining_date: '' }))}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <Select
                            placeholder="From day"
                            value={filters.start_joining_date || undefined}
                            onChange={v => handleFilterChange('start_joining_date', v || '')}
                            allowClear
                            style={{ flex: 1 }}
                            size="small"
                          >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                              <Option key={d} value={String(d)}>{d}</Option>
                            ))}
                          </Select>
                          <span style={{ color: '#aaa' }}>–</span>
                          <Select
                            placeholder="To day"
                            value={filters.end_joining_date || undefined}
                            onChange={v => handleFilterChange('end_joining_date', v || '')}
                            allowClear
                            style={{ flex: 1 }}
                            size="small"
                          >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                              <Option key={d} value={String(d)}>{d}</Option>
                            ))}
                          </Select>
                        </div>
                      </Col>

                      {/* ── Last Payment Date Range ── */}
                      <Col span={8}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 600 }}>
                          Last Payment Date Range
                          {(filters.last_payment_from || filters.last_payment_to) && (
                            <Button
                              size="small"
                              type="link"
                              danger
                              style={{ padding: '0 4px', fontSize: 11 }}
                              onClick={() => setFilters(f => ({ ...f, last_payment_from: '', last_payment_to: '' }))}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <DatePicker.RangePicker
                          size="small"
                          style={{ width: '100%' }}
                          format="DD-MM-YYYY"
                          placeholder={['From date', 'To date']}
                          value={[
                            filters.last_payment_from ? moment(filters.last_payment_from) : null,
                            filters.last_payment_to   ? moment(filters.last_payment_to)   : null,
                          ]}
                          onChange={(dates) => {
                            setFilters(f => ({
                              ...f,
                              last_payment_from: dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
                              last_payment_to:   dates?.[1] ? dates[1].format('YYYY-MM-DD') : '',
                            }));
                          }}
                        />
                        {filters.last_payment_from && filters.last_payment_to && (
                          <div style={{ fontSize: 11, color: '#722ed1', marginTop: 3 }}>
                            Paid between {moment(filters.last_payment_from).format('D MMM')} – {moment(filters.last_payment_to).format('D MMM YYYY')}
                          </div>
                        )}
                      </Col>



                      <Divider />
                      <Col xs={24} style={{ marginBottom: 8 }}>
                        <Tag color={
                          activeTab === 'calling_list' ? 'orange'
                          : activeTab === 'paid_members' ? 'green'
                          : activeTab === 'inactive_members' ? 'default'
                          : 'blue'
                        } style={{ fontSize: 12, padding: '3px 10px' }}>
                          {activeTab === 'calling_list' && 'Overdue members — last payment > 25 days ago'}
                          {activeTab === 'paid_members' && 'Paid members — paid within the last 30 days'}
                          {activeTab === 'inactive_members' && 'Inactive / non-active members'}
                          {activeTab === 'all_members' && 'All active members regardless of payment'}
                        </Tag>
                        <span style={{ marginLeft: 10, color: '#888', fontSize: 12 }}>
                          — Use <strong>Bulk Call</strong> in the header to call this entire group
                        </span>
                      </Col>
                      <Col xs={24} style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                          <span style={{ marginRight: 10, fontSize: 13 }}>Records per page:</span>
                          <Select defaultValue={20} onChange={handleLimitChange}>
                            <Option value={20}>20</Option>
                            <Option value={40}>40</Option>
                            <Option value={60}>60</Option>
                            <Option value={80}>80</Option>
                            <Option value={100}>100</Option>
                          </Select>
                        </div>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => getUserList(currentPage, limit, sortField, sortOrder, search, filters, activeTab)}
                        >
                          Refresh
                        </Button>
                        <Button
                          icon={<ClearOutlined />}
                          danger
                          onClick={() => {
                            const empty = {
                              name: '', email: '', phone: '', father_name: '',
                              caste: '', gotra: '', village: '', district: '',
                              tehsil: '', occ_district: '',
                              announced_amount: '', max_announced_amount: '',
                              start_joining_date: '', end_joining_date: '',
                              last_payment_from: '', last_payment_to: '',
                            };
                            setFilters(empty);
                            setSearch('');
                            setCurrentPage(1);
                            getUserList(1, limit, null, null, '', empty, activeTab);
                          }}
                        >
                          Clear Filters
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
                dataSource={userList.map((user, index) => ({
                  key: (limit * (currentPage - 1)) + index + 1,
                  name: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar
                        shape="square"
                        size={34}
                        src={user.profile_img || userDummy}
                        icon={!user.profile_img && <UserOutlined />}
                        onClick={() => showModal(user.profile_img || userDummy)}
                        style={{ cursor: 'pointer', borderRadius: 4, flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#7a0000' }}>
                          <Link to={`/users/view/${user.user_id}`} style={{ color: '#1890ff' }}>{user.name_hn || user.name}</Link>
                        </div>
                        {(user.relation_hn || user.father_name_hn) && (
                          <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                            {user.relation_hn && (
                              <span style={{ color: '#aaa' }}>{user.relation_hn}{' '}</span>
                            )}
                            {user.father_name_hn}
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                  phone_no: (
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{user.phone_no || '—'}</div>
                      {user.alternate_phone_no && <div style={{ fontSize: 11, color: '#888' }}>{user.alternate_phone_no}</div>}
                    </div>
                  ),
                  per_village: <span style={{ fontSize: 12 }}>{user.per_village || '—'}</span>,
                  per_district: <span style={{ fontSize: 12 }}>{user.per_district || '—'}</span>,
                  occ_name: <span style={{ fontSize: 12 }}>{user.occ_name || '—'}</span>,
                  announced_amount: <span style={{ fontWeight: 600, color: '#faad14' }}>{user.announced_amount ? 'Rs.' + user.announced_amount : '—'}</span>,
                  joining_date: <span style={{ fontSize: 12 }}>{user.joining_date ? moment(user.joining_date).format('DD MMM YYYY') : '—'}</span>,
                  dob: <span style={{ fontSize: 12 }}>{user.dob ? moment(user.dob).format('DD MMM YYYY') : '—'}</span>,
                  gotra: <span style={{ fontSize: 12 }}>{user.gotra || '—'}</span>,
                  latest_payment_response: user,
                  current_year_duration: <span style={{ fontSize: 12 }}>{user.current_year_duration || '—'}</span>,
                  total_duration: <span style={{ fontSize: 12 }}>{user.total_duration || '—'}</span>,
                  end_month: <span style={{ fontSize: 12 }}>{user.end_month ? moment(user.end_month).format('MMM YYYY') : '—'}</span>,
                  last_payment_date: <span style={{ fontSize: 12 }}>{user.last_payment_date ? moment(user.last_payment_date).format('DD MMM YYYY') : '—'}</span>,
                  last_payment_amount: <span style={{ fontWeight: 600, color: '#52c41a' }}>{user.last_payment_amount ? 'Rs.' + user.last_payment_amount : '—'}</span>,
                  total_received_amount: <span style={{ fontWeight: 600, color: '#52c41a' }}>{user.total_received_amount ? 'Rs.' + user.total_received_amount : '—'}</span>,
                  member_status: <span style={{ fontSize: 12 }}>{user.member_status || '—'}</span>,
                  last_payment_status: user.last_payment_status ? (
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, ...statusColor(user.last_payment_status) }}>
                      {user.last_payment_status.charAt(0).toUpperCase() + user.last_payment_status.slice(1)}
                    </span>
                  ) : <span style={{ color: '#ccc' }}>—</span>,



                  action: (
                    <div className="button-container" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {hasPermission('/reports/calling-report', 'editPermission') && (
                        <Tooltip title="View Responses">
                          <button className="update-btn">
                            <EyeOutlined onClick={() => openCallHistory(user)} />
                          </button>
                        </Tooltip>
                      )}
                      {hasPermission('/reports/calling-report', 'editPermission') && (
                        <Tooltip title="Voice Call">
                          <Button
                            size="small"
                            icon={<PhoneOutlined />}
                            style={{ color: '#722ed1', borderColor: '#722ed1' }}
                            onClick={() => openIndividualCallModal({ ...user, name_display: user.name })}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )
                }))}
                pagination={{
                  ...pagination,
                  showTotal: total => `Total ${total} records`,
                  style: { marginRight: 20 }
                }}
                onChange={handleTableChange}
                className="ant-border-space"
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
      >
        <img
          src={currentImage}
          alt="Profile"
          style={{ width: '100%' }}
        />
      </Modal>

      {/* ── Call History Modal ───────────────────────────────────────── */}
      <Modal
        title={
          <span>
            <PhoneOutlined style={{ color: '#722ed1', marginRight: 8 }} />
            Call History — {callHistoryUser?.name || callHistoryUser?.phone_no}
          </span>
        }
        open={callHistoryVisible}
        footer={null}
        onCancel={() => setCallHistoryVisible(false)}
        width={820}
      >
        {callHistoryLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : callHistoryData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
            No voice calls made to this member yet.
          </div>
        ) : (
          <Table
            size="small"
            rowKey="id"
            dataSource={callHistoryData}
            pagination={false}
            columns={[
              { title: 'Campaign', dataIndex: 'campaign_name', key: 'campaign_name', width: 180 },
              {
                title: 'Status', dataIndex: 'status', key: 'status', width: 100,
                render: s => (
                  <Tag color={{ processing: 'orange', completed: 'green', failed: 'red' }[s] || 'default'}>{s}</Tag>
                )
              },
              {
                title: 'Aarvi Response', key: 'aarvi_response', width: 280,
                render: (_, r) => {
                  const d = r.aarvi_response;
                  if (!d) return <span style={{ color: '#ccc' }}>—</span>;
                  const status = d.status || d.Status;
                  const desc   = d.desc   || d.description || d.message || d.Desc;
                  const uid    = r.aarvi_unique_id;
                  return (
                    <div style={{ fontSize: 12 }}>
                      {status && <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>}
                      {desc   && <span style={{ marginLeft: 4 }}>{desc}</span>}
                      {uid    && <div style={{ color: '#888', marginTop: 2 }}>ID: {uid}</div>}
                    </div>
                  );
                }
              },
              {
                title: 'Date', dataIndex: 'created_at', key: 'created_at', width: 140,
                render: t => moment(t).format('DD-MM-YYYY HH:mm')
              },
            ]}
          />
        )}
      </Modal>


      {/* ── Bulk Voice Broadcast Modal ──────────────────────────────────── */}
      <Modal
        title={<span><SoundOutlined style={{ color: '#722ed1', marginRight: 8 }} />Bulk Voice Call Broadcast</span>}
        open={voiceBroadcastVisible}
        onCancel={() => setVoiceBroadcastVisible(false)}
        onOk={handleVoiceBroadcast}
        okText="Start Broadcast"
        okButtonProps={{ loading: vcLoading, style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } }}
        width={580}
      >
        {/* ── Scope selector ── */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Who to Call</label>
          <Radio.Group value={vcScope} onChange={e => handleVcScopeChange(e.target.value)} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Radio.Button value="calling_list" style={{ borderRadius: 4 }}>Calling List</Radio.Button>
            <Radio.Button value="paid_members" style={{ borderRadius: 4 }}>Paid Members</Radio.Button>
            <Radio.Button value="inactive_members" style={{ borderRadius: 4 }}>Inactive</Radio.Button>
            <Radio.Button value="all_members" style={{ borderRadius: 4 }}>All Members</Radio.Button>
            <Radio.Button value="custom_numbers" style={{ borderRadius: 4 }}>Custom Numbers</Radio.Button>
          </Radio.Group>
          <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
            {vcScope === 'calling_list'     && 'Active members whose last payment was more than 25 days ago.'}
            {vcScope === 'paid_members'     && 'Active members who paid within the last 30 days.'}
            {vcScope === 'inactive_members' && 'Members with inactive / non-active status.'}
            {vcScope === 'all_members'      && 'All active members regardless of payment status.'}
            {vcScope === 'custom_numbers'   && 'Enter specific phone numbers below — no DB lookup.'}
          </div>
        </div>

        {/* ── Active filters summary ── */}
        {vcScope !== 'custom_numbers' && (() => {
          const activeFilters = [];
          if (filters.name)               activeFilters.push(`Name: ${filters.name}`);
          if (filters.phone)              activeFilters.push(`Phone: ${filters.phone}`);
          if (filters.father_name)        activeFilters.push(`Father: ${filters.father_name}`);
          if (filters.village)            activeFilters.push(`Village: ${filters.village}`);
          if (filters.district)           activeFilters.push(`District: ${filters.district}`);
          if (filters.tehsil)             activeFilters.push(`Tehsil: ${filters.tehsil}`);
          if (filters.occ_district)       activeFilters.push(`Occ. District: ${filters.occ_district}`);
          if (filters.gotra)              activeFilters.push(`Gotra: ${filters.gotra}`);
          if (filters.caste)              activeFilters.push(`Caste: ${filters.caste}`);
          if (filters.announced_amount || filters.max_announced_amount)
            activeFilters.push(`Share: ${filters.announced_amount || '0'} – ${filters.max_announced_amount || '∞'}`);
          if (filters.start_joining_date || filters.end_joining_date)
            activeFilters.push(`Joining Day: ${filters.start_joining_date || '1'} – ${filters.end_joining_date || '31'}`);
          if (filters.last_payment_from || filters.last_payment_to)
            activeFilters.push(`Last Payment: ${filters.last_payment_from ? moment(filters.last_payment_from).format('D MMM') : '—'} to ${filters.last_payment_to ? moment(filters.last_payment_to).format('D MMM YY') : '—'}`);
          if (!activeFilters.length) return null;
          return (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fffbe6', borderRadius: 6, border: '1px solid #ffe58f' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#ad6800', marginBottom: 4 }}>Active Filters (applied to broadcast):</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {activeFilters.map((f, i) => <Tag key={i} color="gold" style={{ fontSize: 11 }}>{f}</Tag>)}
              </div>
            </div>
          );
        })()}

        {/* ── Filter count badge ── */}
        {vcScope !== 'custom_numbers' && vcFilterCount !== null && (
          <div style={{ marginBottom: 14, padding: '8px 12px', background: '#f0e6ff', borderRadius: 6, border: '1px solid #d3adf7' }}>
            <PhoneOutlined style={{ marginRight: 6, color: '#722ed1' }} />
            <strong>{vcFilterCount}</strong> members match the selected scope + filters and will receive this call.
          </div>
        )}
        {vcScope !== 'custom_numbers' && vcFilterCount === null && (
          <div style={{ marginBottom: 14, padding: '6px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0', fontSize: 12, color: '#aaa' }}>
            <PhoneOutlined style={{ marginRight: 6 }} />Counting matching members...
          </div>
        )}

        {/* ── Custom numbers textarea ── */}
        {vcScope === 'custom_numbers' && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Phone Numbers <span style={{ color: 'red' }}>*</span>
            </label>
            <Input.TextArea
              rows={5}
              placeholder={'Enter one number per line or comma-separated:\n9876543210\n9876543211, 9876543212'}
              value={vcCustomNumbers}
              onChange={e => setVcCustomNumbers(e.target.value)}
            />
            {vcCustomNumbers.trim() && (
              <div style={{ fontSize: 12, color: '#722ed1', marginTop: 4 }}>
                {vcCustomNumbers.split(/[\s,;\n]+/).filter(n => n.replace(/\D/g, '').length >= 10).length} valid numbers entered
              </div>
            )}
          </div>
        )}

        {/* ── Campaign name ── */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Campaign Name</label>
          <Input
            placeholder="e.g. Payment Reminder March 2026"
            value={vcCampaignName}
            onChange={e => setVcCampaignName(e.target.value)}
          />
        </div>

        {/* ── Announcement select ── */}
        <div>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Announcement <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select announcement"
            value={vcAnnouncementId || undefined}
            onChange={v => setVcAnnouncementId(v)}
            optionFilterProp="children"
          >
            {announcements.map(a => (
              <Option key={a.announcement_id} value={a.announcement_id}>
                [{a.announcement_id}] {a.announcement_name || '—'}
              </Option>
            ))}
          </Select>
          {vcAnnouncementId && (
            <div style={{ fontSize: 12, color: '#722ed1', marginTop: 4 }}>Selected ID: <strong>{vcAnnouncementId}</strong></div>
          )}
        </div>
      </Modal>

      {/* ── Individual Voice Call Modal ─────────────────────────────────── */}
      <Modal
        title={<span><PhoneOutlined style={{ color: '#722ed1', marginRight: 8 }} />
          Voice Call — {individualCallUser?.name_display || individualCallUser?.phone_no}
        </span>}
        open={individualCallVisible}
        onCancel={() => setIndividualCallVisible(false)}
        onOk={handleIndividualCall}
        okText="Call Now"
        okButtonProps={{ loading: icLoading, style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } }}
        width={460}
      >
        {individualCallUser && (
          <div style={{ marginBottom: 14, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #e8e8e8' }}>
            <div><strong>Phone:</strong> {individualCallUser.phone_no}</div>
          </div>
        )}
        <div>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Announcement <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select announcement"
            value={icAnnouncementId || undefined}
            onChange={v => setIcAnnouncementId(v)}
            optionFilterProp="children"
          >
            {announcements.map(a => (
              <Option key={a.announcement_id} value={a.announcement_id}>
                [{a.announcement_id}] {a.announcement_name || '—'}
              </Option>
            ))}
          </Select>
          {icAnnouncementId && (
            <div style={{ fontSize: 12, color: '#722ed1', marginTop: 4 }}>Selected ID: <strong>{icAnnouncementId}</strong></div>
          )}
        </div>
      </Modal>

      {/* ── Campaigns History Modal ─────────────────────────────────────── */}
      <Modal
        title={<span><HistoryOutlined style={{ marginRight: 8 }} />Voice Call Campaigns</span>}
        open={campaignsVisible}
        onCancel={() => setCampaignsVisible(false)}
        footer={null}
        width={900}
      >
        <Table
          loading={campaignsLoading}
          size="small"
          rowKey="id"
          dataSource={campaigns}
          pagination={{
            ...campaignsPagination,
            onChange: (page, pageSize) => loadCampaigns(page, pageSize),
            showTotal: total => `Total ${total} campaigns`
          }}
          columns={[
            { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
            { title: 'Campaign Name', dataIndex: 'campaign_name', key: 'campaign_name', width: 180 },
            {
              title: 'Type', dataIndex: 'call_type', key: 'call_type', width: 90,
              render: t => <Tag color={t === 'bulk' ? 'purple' : 'blue'}>{t}</Tag>
            },
            { title: 'Contacts', dataIndex: 'contact_count', key: 'contact_count', width: 80 },
            {
              title: 'Status', dataIndex: 'status', key: 'status', width: 100,
              render: s => {
                const colorMap = { processing: 'orange', completed: 'green', failed: 'red', pending: 'default' };
                return <Tag color={colorMap[s] || 'default'}>{s}</Tag>;
              }
            },
            {
              title: 'Date', dataIndex: 'created_at', key: 'created_at', width: 140,
              render: t => moment(t).format('DD-MM-YYYY HH:mm')
            },
            {
              title: 'Action', key: 'action', width: 80,
              render: (_, record) => (
                <Button size="small" onClick={() => fetchCampaignReport(record.id)}>Report</Button>
              )
            }
          ]}
        />
      </Modal>

      {/* ── Campaign Report Modal ───────────────────────────────────────── */}
      <Modal
        title="Voice Call Report"
        open={reportVisible}
        onCancel={() => setReportVisible(false)}
        footer={null}
        width={700}
      >
        {reportLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : reportData ? (
          <div>
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f2f5', borderRadius: 6 }}>
              <strong>{reportData.campaign?.campaign_name}</strong>
              <span style={{ marginLeft: 16, color: '#888' }}>Contacts: {reportData.campaign?.contact_count}</span>
              <span style={{ marginLeft: 16, color: '#888' }}>Date: {moment(reportData.campaign?.created_at).format('DD-MM-YYYY HH:mm')}</span>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Aarvi Unique ID: {reportData.campaign?.aarvi_unique_id}</div>
            <pre style={{ background: '#fafafa', padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(reportData.report, null, 2)}
            </pre>
          </div>
        ) : null}
      </Modal>

    </div>
    </>
  );
}

export default CallingReport;
